// Template storage for the print studio.
//
// Server: /api/v1/print-templates (docs/api/print-templates.md).
//   - company templates (managers), personal templates, admin settings,
//     visibility per report permission — all enforced by the server.
// Local fallback (localStorage): used only for reports the server doesn't
// know yet (it answers 422 on `report`), so printing keeps working while a
// new report key is being registered by the backend.
// "Last used template" always stays in the browser.
//
// All functions take the calling component (`vm`) for $axios / $endpoint.

var PREFIX = 'abr-print-templates:';

function local(key, value) {
  try {
    if (value === undefined) return JSON.parse(localStorage.getItem(PREFIX + key)) || null;
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch (e) {
    return null;
  }
  return null;
}

function url(vm, path) {
  return vm.$endpoint + 'print-templates' + (path || '');
}

var JSON_HEADERS = { headers: { Accept: 'application/json' } };

// Reports registered on the backend (docs/api/print-templates.md). Any other
// report key never touches the server: its templates stay in localStorage.
// Add a key here once the backend registers it.
export var SERVER_REPORTS = ['accounting-docs', 'accounting-doc'];

function localResult(report, fallbackManage) {
  return {
    source: 'local',
    canManage: !!fallbackManage,
    settings: localSettings(report),
    company: localList(report, 'company'),
    user: localList(report, 'user'),
  };
}

// Server item -> studio template (template JSON + id/name).
function fromServer(item) {
  var t = Object.assign({}, item.template || {});
  t.id = item.id;
  t.name = item.name;
  t.kind = item.kind || t.kind || 'list';
  t.builtIn = false;
  t.updatedAt = item.updated_at;
  if (item.updated_by) t.updatedBy = item.updated_by;
  return t;
}

// Studio template -> JSON body (strip identity fields the server owns).
function templateBody(tpl) {
  var t = JSON.parse(JSON.stringify(tpl));
  delete t.id;
  delete t.name;
  delete t.builtIn;
  delete t.updatedAt;
  delete t.updatedBy;
  return t;
}

function isUnknownReport(err) {
  var r = err && err.response;
  return !!(r && r.status === 422 && r.data && r.data.errors && r.data.errors.report);
}

// ---------- local fallback ----------
function localList(report, scope) {
  return (local(report + ':' + scope) || []).slice();
}
function localSettings(report) {
  return Object.assign({ defaultId: null, lockedIds: [], allowUserTemplates: true }, local(report + ':settings') || {});
}
function localSave(report, scope, tpl) {
  var list = localList(report, scope);
  var copy = JSON.parse(JSON.stringify(tpl));
  if (!copy.id) copy.id = 'local-' + Date.now().toString(36);
  copy.updatedAt = new Date().toISOString();
  var i = list.findIndex(function (t) { return t.id === copy.id; });
  if (i === -1) list.push(copy);
  else list.splice(i, 1, copy);
  local(report + ':' + scope, list);
  return copy;
}

// ---------- public API ----------

// → { source: 'server'|'local', canManage, settings{defaultId,lockedIds,allowUserTemplates}, company[], user[] }
export function load(vm, report, fallbackManage) {
  if (SERVER_REPORTS.indexOf(report) === -1) return Promise.resolve(localResult(report, fallbackManage));
  return vm.$axios
    .get(url(vm), Object.assign({ params: { report: report } }, JSON_HEADERS))
    .then(function (res) {
      var d = (res.data && res.data.data) || {};
      var s = d.settings || {};
      return {
        source: 'server',
        canManage: !!d.can_manage,
        settings: {
          defaultId: s.default_id == null ? null : s.default_id,
          lockedIds: s.locked_ids || [],
          allowUserTemplates: s.allow_user_templates !== false,
        },
        company: (d.company || []).map(fromServer),
        user: (d.user || []).map(fromServer),
      };
    })
    .catch(function (err) {
      if (!isUnknownReport(err)) throw err;
      return localResult(report, fallbackManage);
    });
}

// Create (no id / local id) or update (server id). Resolves the saved template.
export function save(vm, ctx, scope, tpl) {
  if (ctx.source === 'local') return Promise.resolve(localSave(ctx.report, scope, tpl));
  var body = templateBody(tpl);
  if (tpl.id && typeof tpl.id === 'number') {
    return vm.$axios
      .put(url(vm, '/' + tpl.id), { name: tpl.name, template: body }, JSON_HEADERS)
      .then(function (res) { return fromServer(res.data.data); });
  }
  return vm.$axios
    .post(url(vm), { report: ctx.report, scope: scope, name: tpl.name, kind: body.kind || 'list', template: body }, JSON_HEADERS)
    .then(function (res) { return fromServer(res.data.data); });
}

export function remove(vm, ctx, scope, id) {
  if (ctx.source === 'local') {
    local(ctx.report + ':' + scope, localList(ctx.report, scope).filter(function (t) { return t.id !== id; }));
    return Promise.resolve();
  }
  return vm.$axios.delete(url(vm, '/' + id), JSON_HEADERS);
}

// settings: { defaultId, lockedIds, allowUserTemplates }
export function saveSettings(vm, ctx, settings) {
  if (ctx.source === 'local') {
    local(ctx.report + ':settings', settings);
    return Promise.resolve(settings);
  }
  return vm.$axios
    .put(
      url(vm, '/settings'),
      {
        report: ctx.report,
        default_id: settings.defaultId,
        locked_ids: settings.lockedIds,
        allow_user_templates: settings.allowUserTemplates,
      },
      JSON_HEADERS
    )
    .then(function (res) {
      var d = res.data.data || {};
      return {
        defaultId: d.default_id == null ? null : d.default_id,
        lockedIds: d.locked_ids || [],
        allowUserTemplates: d.allow_user_templates !== false,
      };
    });
}

export function lastUsed(report, id) {
  if (id === undefined) return local(report + ':last');
  local(report + ':last', id);
}

// Human-readable message for a failed call (403/404/422 bodies carry `message`).
export function errorMessage(err) {
  var d = err && err.response && err.response.data;
  if (d && d.errors) {
    var first = Object.keys(d.errors)[0];
    if (first && d.errors[first] && d.errors[first][0]) return d.errors[first][0];
  }
  return (d && d.message) || (err && err.message) || '';
}
